/**
 * Reusable proposal preview + bulk-select UI. Lives separately from
 * `DiscoveryDialog` so the wizard's final-review step can render the
 * same widget without nesting dialogs or duplicating ~100 lines of
 * grouping/checkbox/quick-select logic.
 *
 * Exports:
 *   - `selectionKey(p)` — stable key per proposal (componentSlug | kind | slug).
 *     Used as the set-membership identity for the parent's selection state.
 *   - `selectionsFromKeys(set, proposals)` — projects the operator's selection
 *     set back into the API's apply payload shape.
 *   - `DiscoveryPreview` — controlled component that renders the proposal
 *     list with grouping, totals, bulk-select toolbar, errors + warnings,
 *     and per-row checkboxes. Parent owns the selection set + actions.
 *   - `defaultSelection(proposals)` — the standard "pre-tick creates +
 *     updates, leave removes off" auto-selection rule. Shared so both
 *     consumers default to the same destructive-opt-in behaviour.
 */
import { useMemo } from 'react';
import {
  AlertCircle,
  Boxes,
  Check,
  Network,
  Pencil,
  Plus,
  Server as ServerIcon,
  Trash2,
} from 'lucide-react';
import type {
  DiscoveryProposal,
  DiscoveryReport,
  DiscoverySelection,
} from '@/api/client.js';
import { cn } from '@/lib/utils.js';

/** Stable set-membership key for a proposal. */
export function selectionKey(p: DiscoveryProposal): string {
  if (p.kind === 'create-component') {
    return `${p.componentSlug}|create-component`;
  }
  if (p.kind === 'create-server') {
    return `__server|${p.spec.slug}`;
  }
  if (p.kind === 'create-access-method') {
    return p.proposalKey;
  }
  const slug = p.kind === 'remove' ? p.existing.slug : p.spec.slug;
  return `${p.componentSlug}|${p.kind}|${slug}`;
}

/** Project the operator's tick state back into the API's apply payload. */
export function selectionsFromKeys(
  selected: Set<string>,
  proposals: DiscoveryProposal[],
): DiscoverySelection[] {
  const out: DiscoverySelection[] = [];
  for (const p of proposals) {
    const key = selectionKey(p);
    if (!selected.has(key)) continue;
    if (p.kind === 'noop') continue;
    if (p.kind === 'create-component') {
      out.push({ componentSlug: p.componentSlug, action: 'create-component' });
      continue;
    }
    if (p.kind === 'create-server') {
      out.push({ serverSlug: p.spec.slug, action: 'create-server' });
      continue;
    }
    if (p.kind === 'create-access-method') {
      out.push({
        accessMethodSlug: p.spec.slug,
        action: 'create-access-method',
      });
      continue;
    }
    out.push({
      componentSlug: p.componentSlug,
      scriptSlug: p.kind === 'remove' ? p.existing.slug : p.spec.slug,
      action: p.kind,
    });
  }
  return out;
}

/**
 * Standard initial selection: pre-tick `create`, `update`, and
 * `create-component`; leave `remove` and `noop` off. Removes are
 * destructive — explicit opt-in beats default-on. `create-component`
 * is non-destructive (just adds a row) so it's safe to tick by
 * default.
 */
export function defaultSelection(proposals: DiscoveryProposal[]): Set<string> {
  const next = new Set<string>();
  for (const p of proposals) {
    if (
      p.kind === 'create' ||
      p.kind === 'update' ||
      p.kind === 'create-component' ||
      p.kind === 'create-server' ||
      p.kind === 'create-access-method'
    ) {
      next.add(selectionKey(p));
    }
  }
  return next;
}

interface Props {
  report: DiscoveryReport;
  selected: Set<string>;
  toggle: (key: string) => void;
  onSelectAll: () => void;
  onSelectByKind: (k: 'create' | 'update' | 'remove') => void;
  onSelectNone: () => void;
  /**
   * Override the empty-state message rendered when the report has no
   * proposals / errors / warnings. The dialog uses the default "in
   * sync" copy; the wizard's final-review step prefers a
   * congratulatory "nothing else to suggest" framing.
   */
  emptyStateMessage?: React.ReactNode;
}

export function DiscoveryPreview({
  report,
  selected,
  toggle,
  onSelectAll,
  onSelectByKind,
  onSelectNone,
  emptyStateMessage,
}: Props): JSX.Element {
  // Infrastructure proposals (server + access methods) get their own pinned
  // group at the top. Everything else groups by componentSlug as before.
  // The reconciler's sort already puts servers ahead of access methods ahead
  // of per-component proposals, but we re-group here so the render can
  // bracket them with a clearer "Infrastructure" header.
  const { infraProposals, grouped } = useMemo(() => {
    const infra: DiscoveryProposal[] = [];
    const byComponent = new Map<string, DiscoveryProposal[]>();
    for (const p of report.proposals) {
      if (p.kind === 'create-server' || p.kind === 'create-access-method') {
        infra.push(p);
        continue;
      }
      const arr = byComponent.get(p.componentSlug) ?? [];
      arr.push(p);
      byComponent.set(p.componentSlug, arr);
    }
    return {
      infraProposals: infra,
      grouped: [...byComponent.entries()].sort(([a], [b]) => a.localeCompare(b)),
    };
  }, [report.proposals]);

  const hasAnything =
    report.proposals.length > 0 || report.errors.length > 0 || report.warnings.length > 0;

  // Per-kind quick-select buttons hide when that kind's count is 0.
  // Keeps the toolbar lean to what's actually on offer.
  const hasCreate = report.totals.create > 0;
  const hasUpdate = report.totals.update > 0;
  const hasRemove = report.totals.remove > 0;
  const hasCreateComponent = report.totals.createComponent > 0;
  const hasInfra =
    (report.totals.createServer ?? 0) > 0 ||
    (report.totals.createAccessMethod ?? 0) > 0;
  const hasSelectable = hasCreate || hasUpdate || hasRemove || hasCreateComponent || hasInfra;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {(report.totals.createServer ?? 0) > 0 && (
          <>
            <span className="text-orange-600 dark:text-orange-400">
              {report.totals.createServer} new server
              {report.totals.createServer === 1 ? '' : 's'}
            </span>
            {' · '}
          </>
        )}
        {(report.totals.createAccessMethod ?? 0) > 0 && (
          <>
            <span className="text-cyan-600 dark:text-cyan-400">
              {report.totals.createAccessMethod} new access method
              {report.totals.createAccessMethod === 1 ? '' : 's'}
            </span>
            {' · '}
          </>
        )}
        {report.totals.createComponent > 0 && (
          <>
            <span className="text-purple-600 dark:text-purple-400">
              {report.totals.createComponent} new component
              {report.totals.createComponent === 1 ? '' : 's'}
            </span>
            {' · '}
          </>
        )}
        <span className="text-emerald-600 dark:text-emerald-400">{report.totals.create} create</span>
        {' · '}
        <span className="text-blue-600 dark:text-blue-400">{report.totals.update} update</span>
        {' · '}
        <span className="text-red-600 dark:text-red-400">{report.totals.remove} remove</span>
        {' · '}
        <span>{report.totals.noop} unchanged</span>
        {report.totals.warnings > 0 && ` · ${report.totals.warnings} warnings`}
        {report.totals.errors > 0 && ` · ${report.totals.errors} errors`}
      </p>

      {hasSelectable && (
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px]">
          <span className="mr-1 text-muted-foreground">Quick select:</span>
          <BulkSelectButton onClick={onSelectAll}>All</BulkSelectButton>
          {hasCreate && (
            <BulkSelectButton onClick={() => onSelectByKind('create')}>
              Creates ({report.totals.create})
            </BulkSelectButton>
          )}
          {hasUpdate && (
            <BulkSelectButton onClick={() => onSelectByKind('update')}>
              Updates ({report.totals.update})
            </BulkSelectButton>
          )}
          {hasRemove && (
            <BulkSelectButton onClick={() => onSelectByKind('remove')}>
              Removes ({report.totals.remove})
            </BulkSelectButton>
          )}
          <BulkSelectButton onClick={onSelectNone}>None</BulkSelectButton>
        </div>
      )}

      {!hasAnything &&
        (emptyStateMessage ?? (
          <p className="rounded-md border border-border bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
            No <span className="font-mono">.deployit/</span> changes detected. The repo and the
            application are in sync.
          </p>
        ))}

      {report.errors.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
            Errors
          </p>
          {report.errors.map((e, i) => (
            <div
              key={i}
              className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
              <div className="min-w-0">
                <p className="font-mono">{e.filePath}</p>
                <pre className="mt-1 whitespace-pre-wrap text-red-600 dark:text-red-400">
                  {e.message}
                </pre>
              </div>
            </div>
          ))}
        </div>
      )}

      {report.warnings.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
            Warnings
          </p>
          {report.warnings.map((w, i) => (
            <div
              key={i}
              className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs"
            >
              {w.message}
            </div>
          ))}
        </div>
      )}

      {infraProposals.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Infrastructure
          </p>
          <div className="rounded-md border border-border">
            {infraProposals.map((p) => (
              <ProposalRow
                key={selectionKey(p)}
                proposal={p}
                selected={selected}
                toggle={toggle}
              />
            ))}
          </div>
        </div>
      )}

      {grouped.map(([componentSlug, proposals]) => (
        <div key={componentSlug} className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {componentSlug}
          </p>
          <div className="rounded-md border border-border">
            {proposals.map((p) => (
              <ProposalRow
                key={selectionKey(p)}
                proposal={p}
                selected={selected}
                toggle={toggle}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * One checkbox row inside a component (or infrastructure) group. Extracted
 * so the same shape renders for every proposal kind — the per-kind label /
 * sub-line shaping happens inline below.
 */
function ProposalRow({
  proposal: p,
  selected,
  toggle,
}: {
  proposal: DiscoveryProposal;
  selected: Set<string>;
  toggle: (key: string) => void;
}): JSX.Element {
  const key = selectionKey(p);
  const checkable = p.kind !== 'noop';
  // Row label / sub-line:
  //  - create-component: render the suggested name + role, with a
  //    "<n> scripts pending after create" hint so the operator
  //    knows what follows.
  //  - create-server: hostname + slug, with "Server `<slug>` on this org" sub-line.
  //  - create-access-method: name + kind + composition hint.
  //  - script kinds: script slug + optional description + change list.
  let rowMain: React.ReactNode;
  let rowSub: React.ReactNode = null;
  if (p.kind === 'create-component') {
    rowMain = (
      <>
        <span className="font-mono">{p.componentSlug}</span>
        <span className="text-[11px] text-muted-foreground">
          create component · {p.suggestedRole}
        </span>
      </>
    );
    rowSub = (
      <>
        Will create <span className="font-mono">{p.suggestedName}</span>; the{' '}
        {p.pendingScriptCount} script{p.pendingScriptCount === 1 ? '' : 's'} under this slug
        land on the next scan.
      </>
    );
  } else if (p.kind === 'create-server') {
    rowMain = (
      <>
        <span className="font-mono">{p.spec.slug}</span>
        <span className="text-[11px] text-muted-foreground">
          create server · {p.spec.hostname}
        </span>
      </>
    );
    rowSub = (
      <>
        Will create the Server <span className="font-mono">{p.spec.name}</span> on this org,
        pointing at <span className="font-mono">{p.spec.hostname}</span>.
      </>
    );
  } else if (p.kind === 'create-access-method') {
    const transportHint =
      p.transport === null
        ? 'leaf (no inner transport)'
        : 'existingId' in p.transport
          ? 'composes on existing transport'
          : 'composes on the new SSH leaf above';
    rowMain = (
      <>
        <span className="font-mono">{p.spec.slug}</span>
        <span className="text-[11px] text-muted-foreground">
          create access method · {p.spec.kind} · {transportHint}
        </span>
      </>
    );
    rowSub = p.spec.description ?? null;
  } else if (p.kind === 'remove') {
    rowMain = <span className="font-mono">{p.existing.slug}</span>;
  } else {
    // create / update / noop — all carry `spec` with `slug` + optional `description`.
    rowMain = (
      <>
        <span className="font-mono">{p.spec.slug}</span>
        {p.kind === 'update' && (
          <span className="text-[11px] text-muted-foreground">
            changes: {p.changedFields.join(', ')}
          </span>
        )}
      </>
    );
    rowSub = p.spec.description ?? null;
  }
  return (
    <label
      className={cn(
        'flex items-start gap-2 border-b border-border px-2.5 py-2 last:border-b-0',
        checkable && 'cursor-pointer hover:bg-accent/40',
      )}
    >
      <input
        type="checkbox"
        className="mt-1 h-3.5 w-3.5"
        checked={selected.has(key)}
        onChange={() => toggle(key)}
        disabled={!checkable}
      />
      <span className="flex-1 space-y-0.5">
        <span className="flex items-center gap-1.5 text-sm">
          <ProposalIcon kind={p.kind} />
          {rowMain}
        </span>
        {rowSub && (
          <span className="block text-[11px] text-muted-foreground">{rowSub}</span>
        )}
      </span>
    </label>
  );
}

function ProposalIcon({ kind }: { kind: DiscoveryProposal['kind'] }): JSX.Element {
  if (kind === 'create') return <Plus className="h-3.5 w-3.5 text-emerald-500" />;
  if (kind === 'update') return <Pencil className="h-3.5 w-3.5 text-blue-500" />;
  if (kind === 'remove') return <Trash2 className="h-3.5 w-3.5 text-red-500" />;
  if (kind === 'create-component') return <Boxes className="h-3.5 w-3.5 text-purple-500" />;
  if (kind === 'create-server') return <ServerIcon className="h-3.5 w-3.5 text-orange-500" />;
  if (kind === 'create-access-method') return <Network className="h-3.5 w-3.5 text-cyan-500" />;
  return <Check className="h-3.5 w-3.5 text-muted-foreground" />;
}

/**
 * Tight pill button for the bulk-select toolbar. Sized smaller and
 * quieter than the design-system Button so several fit on one row
 * without dominating the toolbar.
 */
function BulkSelectButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-border bg-background px-2 py-0.5 text-[11px] hover:bg-accent/40"
    >
      {children}
    </button>
  );
}
